import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../../../api/axiosInstance";
import { BLOG_NEW_ROUTE, blogEditRoute, blogApi as sharedBlogApi } from "./blogShared"

const ADMIN_BLOG_ROUTE = "/superadmin/blogs";


const extractError = (data) => {
    if (!data) return "";
    if (typeof data === "string") return data;
    if (data.message) return data.message;
    if (data.detail) return data.detail;
    return Object.entries(data)
        .map(([k, v]) => `${k}: ${[].concat(v).join(", ")}`)
        .join(" | ");
};

async function request(path, { method = "GET", body } = {}) {
    try {
        const { data } = await axiosInstance.request({
            url: path,
            method,
            data: body,
            ...(body instanceof FormData && {
                headers: { "Content-Type": "multipart/form-data" },
            }),
        });
        if (data?.success === false) throw new Error(extractError(data));
        return data;
    } catch (err) {
        throw new Error(extractError(err?.response?.data) || err.message || "Request failed");
    }
}

const blogApi = {
    list: () => request("/api/superadmin/blog-page-master/"),
    create: (fd) => request("/api/superadmin/blog-master-page/", { method: "POST", body: fd }),
    update: (slug, fd) => request(`/api/superadmin/blogs/${slug}`, { method: "PATCH", body: fd }),
    remove: (slug) => request(`/api/superadmin/blog-page-master/${slug}/`, { method: "DELETE" }),
};

const slugify = (s) =>
    s.toLowerCase().trim().replace(/[^a-z0-9\s-]/g, "").replace(/\s+/g, "-").replace(/-+/g, "-");

const fmtDate = (d) =>
    d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "-";

/* ------------------------------------------------------------------ */
/* Small shared UI                                                      */
/* ------------------------------------------------------------------ */
const Spinner = ({ className = "h-5 w-5" }) => (
    <span className={`inline-block animate-spin rounded-full border-2 border-current border-t-transparent ${className}`} />
);

function Toast({ toast, onClose }) {
    useEffect(() => {
        if (!toast) return;
        const t = setTimeout(onClose, 3500);
        return () => clearTimeout(t);
    }, [toast, onClose]);
    if (!toast) return null;
    const color = toast.type === "error" ? "bg-red-600" : "bg-emerald-600";
    return (
        <div role="status" className={`fixed right-4 top-4 z-50 max-w-sm rounded-lg px-4 py-3 text-sm text-white shadow-lg ${color}`}>
            {toast.message}
        </div>
    );
}

function ConfirmModal({ blog, busy, onCancel, onConfirm }) {
    if (!blog) return null;
    return (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4">
            <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
                <h3 className="text-lg font-semibold text-gray-900">Delete blog?</h3>
                <p className="mt-2 text-sm text-gray-600">
                    “{blog.title}” will be permanently removed. This can’t be undone.
                </p>
                <div className="mt-6 flex justify-end gap-3">
                    <button onClick={onCancel} disabled={busy} className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                        Cancel
                    </button>
                    <button onClick={onConfirm} disabled={busy} className="flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-60">
                        {busy && <Spinner className="h-4 w-4" />} Delete
                    </button>
                </div>
            </div>
        </div>
    );
}


function StatusModal({ blog, busy, onCancel, onConfirm }) {
    if (!blog) return null;
    const publishing = !blog.is_published;
    return (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4">
            <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
                <h3 className="text-lg font-semibold text-gray-900">
                    {publishing ? "Publish blog?" : "Move to draft?"}
                </h3>
                <p className="mt-2 text-sm text-gray-600">
                    “{blog.title}” {publishing ? "will be visible to everyone on the website." : "will be hidden from the website."}
                </p>
                <div className="mt-6 flex justify-end gap-3">
                    <button onClick={onCancel} disabled={busy} className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                        Cancel
                    </button>
                    <button onClick={onConfirm} disabled={busy} className="flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-60">
                        {busy && <Spinner className="h-4 w-4" />} {publishing ? "Publish" : "Move to draft"}
                    </button>
                </div>
            </div>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* List page                                                            */
/* ------------------------------------------------------------------ */
export default function BlogPageMaster() {
    const navigate = useNavigate();
    const [blogs, setBlogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [query, setQuery] = useState("");
    const [toDelete, setToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [toToggle, setToToggle] = useState(null)
    const [toggling, setToggling] = useState(false)

    const [toast, setToast] = useState(null);
    const closeToast = useCallback(() => setToast(null), []);

    const load = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const res = await blogApi.list();
            setBlogs((res.data || []).filter((b) => b.is_active !== false))
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return blogs;
        return blogs.filter((b) =>
            [b.title, b.slug, b.industry_name, b.author].some((v) => (v || "").toLowerCase().includes(q))
        );
    }, [blogs, query]);

    const confirmDelete = async () => {
        setDeleting(true);
        try {
            await blogApi.remove(toDelete.slug);
            // setBlogs((prev) => prev.map((b) => (b.slug === toDelete.slug ? { ...b, is_published: false } : b)));
            // setToast({ type: "success", message: "Blog unpublished" });
            setBlogs((prev) => prev.filter((b) => b.slug !== toDelete.slug));
            setToast({ type: "success", message: "Blog deleted" });
            setToDelete(null);
        } catch (e) {
            setToast({ type: "error", message: e.message });
        } finally {
            setDeleting(false);
        }
    };

    const confirmToggle = async () => {
        const next = !toToggle.is_published;
        setToggling(true);
        try {
            const fd = new FormData();
            fd.append("is_published", String(next));
            fd.append("title", toToggle.title || "");
            fd.append("slug", toToggle.slug);
            fd.append("industry", toToggle.industry_id ?? "");
            fd.append("short_description", toToggle.short_description || "");
 fd.append("content", toToggle.content || "");
            fd.append("author", toToggle.author || "");
              await sharedBlogApi.update(toToggle.slug, fd);

            setBlogs((prev) => prev.map((b) => (b.slug === toToggle.slug ? { ...b, is_published: next } : b)));
            setToast({ type: "success", message: next ? "Blog published" : "Blog moved to draft" });
            setToToggle(null);
        } catch (e) {
            setToast({ type: "error", message: e.message });
        } finally {
            setToggling(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 p-4 sm:p-8">
            <Toast toast={toast} onClose={closeToast} />
            <ConfirmModal blog={toDelete} busy={deleting} onCancel={() => setToDelete(null)} onConfirm={confirmDelete} />
            <StatusModal blog={toToggle} busy={toggling} onCancel={() => setToToggle(null)} onConfirm={confirmToggle} />

            <div className="mx-auto max-w-6xl">
                <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <h1 className="text-2xl font-semibold text-gray-900">Blogs</h1>
                        <p className="text-sm text-gray-500">{blogs.length} total</p>
                    </div>
                    <button
                        onClick={() => navigate(BLOG_NEW_ROUTE)}
                        className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
                    >
                        Create blog
                    </button>
                </div>

                <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search by title, slug, industry or author"
                    className="mb-4 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 sm:max-w-sm"
                />

                <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                    {loading ? (
                        <div className="flex items-center justify-center gap-3 py-16 text-gray-500">
                            <Spinner /> Loading blogs…
                        </div>
                    ) : error ? (
                        <div className="py-16 text-center">
                            <p className="text-sm text-red-600">{error}</p>
                            <button onClick={load} className="mt-3 text-sm font-medium text-indigo-600 hover:underline">
                                Try again
                            </button>
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="py-16 text-center text-sm text-gray-500">
                            {blogs.length ? "No blogs match your search." : "No blogs yet. Create your first blog."}
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200 text-sm">
                                <thead className="bg-gray-50 text-left text-xs font-medium text-gray-500">
                                    <tr>
                                        <th className="px-4 py-3">Blog</th>
                                        <th className="px-4 py-3">Industry</th>
                                        <th className="px-4 py-3">Author</th>
                                        <th className="px-4 py-3">Status</th>
                                        <th className="px-4 py-3">Updated</th>
                                        <th className="px-4 py-3 text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {filtered.map((b) => (
                                        <tr key={b.slug} className="hover:bg-gray-50">
                                            <td className="px-4 py-3">
                                                <div className="flex items-center gap-3">
                                                    {b.featured_image ? (
                                                        <img src={b.featured_image} alt="" className="h-12 w-16 flex-none rounded object-cover" />
                                                    ) : (
                                                        <div className="h-12 w-16 flex-none rounded bg-gray-100" />
                                                    )}
                                                    <div className="min-w-0">
                                                        <Link to={blogEditRoute(b.slug)} className="line-clamp-1 max-w-xs font-medium text-gray-900 hover:text-indigo-600">
                                                            {b.title}
                                                        </Link>
                                                        <p className="truncate text-xs text-gray-500">/{b.slug}</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 text-gray-700">{b.industry_name || "-"}</td>
                                            <td className="px-4 py-3 text-gray-700">{b.author || "-"}</td>
                                            <td className="px-4 py-3">
                                                {/* <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${b.is_published ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-600"}`}>
                                                    {b.is_published ? "Published" : "Draft"}
                                                </span> */}
                                                <button
                                                    type="button"
                                                    onClick={() => setToToggle(b)}
                                                    title={b.is_published ? "Click to move to draft" : "Click to publish"}
                                                    className={`cursor-pointer rounded-full px-2.5 py-1 text-xs font-medium transition hover:ring-2 hover:ring-indigo-200 ${b.is_published ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-600"}`}
                                                >
                                                    {b.is_published ? "Published" : "Draft"}
                                                </button>
                                            </td>
                                            <td className="whitespace-nowrap px-4 py-3 text-gray-500">{fmtDate(b.updated_at)}</td>
                                            <td className="whitespace-nowrap px-4 py-3 text-right">
                                                <Link to={blogEditRoute(b.slug)} className="mr-4 font-medium text-indigo-600 hover:underline">
                                                    Edit
                                                </Link>
                                                <button onClick={() => setToDelete(b)} className="font-medium text-red-600 hover:underline">
                                                    Delete
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* Create / Edit page  (/new  and  /:slug)                              */
/* ------------------------------------------------------------------ */
const EMPTY = {
    title: "",
    slug: "",
    industry_id: "",
    short_description: "",
    content: "",
    author: "",
    is_published: false,
};

const inputCls =
    "w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500";

function Field({ label, hint, error, children }) {
    return (
        <label className="block">
            <span className="mb-1 block text-sm font-medium text-gray-700">{label}</span>
            {children}
            {hint && !error && <span className="mt-1 block text-xs text-gray-500">{hint}</span>}
            {error && <span className="mt-1 block text-xs text-red-600">{error}</span>}
        </label>
    );
}

export function BlogEditor() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const isEdit = Boolean(slug) && slug !== "new";

    const [form, setForm] = useState(EMPTY);
    const [slugTouched, setSlugTouched] = useState(false);
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState("");
    const [loading, setLoading] = useState(isEdit);
    const [saving, setSaving] = useState(false);
    const [loadError, setLoadError] = useState("");
    const [errors, setErrors] = useState({});
    const [toast, setToast] = useState(null);
    const closeToast = useCallback(() => setToast(null), []);

    // Edit mode: no detail endpoint, so pick the blog from the list by slug
    useEffect(() => {
        if (!isEdit) return;
        let alive = true;
        (async () => {
            setLoading(true);
            setLoadError("");
            try {
                const res = await blogApi.list();
                // const blog = (res.data || []).find((b) => b.slug === slug);
                const blog = (res.data || []).find((b) => b.slug === slug && b.is_active !== false);
                if (!alive) return;
                if (!blog) return setLoadError("Blog not found.");
                setForm({
                    title: blog.title || "",
                    slug: blog.slug || "",
                    industry_id: blog.industry_id ?? "",
                    short_description: blog.short_description || "",
                    content: blog.content || "",
                    author: blog.author || "",
                    is_published: Boolean(blog.is_published),
                });
                setImagePreview(blog.featured_image || "");
                setSlugTouched(true);
            } catch (e) {
                if (alive) setLoadError(e.message);
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => {
            alive = false;
        };
    }, [isEdit, slug]);

    useEffect(
        () => () => {
            if (imageFile && imagePreview.startsWith("blob:")) URL.revokeObjectURL(imagePreview);
        },
        [imageFile, imagePreview]
    );

    const set = (k) => (e) => {
        const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
        setForm((f) => {
            const next = { ...f, [k]: value };
            if (k === "title" && !slugTouched && !isEdit) next.slug = slugify(value);
            return next;
        });
        setErrors((er) => ({ ...er, [k]: undefined }));
    };

    const onImage = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setImageFile(file);
        setImagePreview(URL.createObjectURL(file));
    };

    const validate = () => {
        const er = {};
        if (!form.title.trim()) er.title = "Title is required";
        if (!form.slug.trim()) er.slug = "Slug is required";
        if (!form.industry_id) er.industry_id = "Industry ID is required";
        if (!form.short_description.trim()) er.short_description = "Short description is required";
        if (!form.content.trim()) er.content = "Content is required";
        if (!form.author.trim()) er.author = "Author is required";
        if (!isEdit && !imageFile) er.featured_image = "Featured image is required";
        setErrors(er);
        return Object.keys(er).length === 0;
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;
        const fd = new FormData();
        Object.entries(form).forEach(([k, v]) => fd.append(k, typeof v === "boolean" ? String(v) : v));
        if (imageFile) fd.append("featured_image", imageFile);

        setSaving(true);
        try {
            if (isEdit) {
                await blogApi.update(slug, fd);
                setToast({ type: "success", message: "Changes saved" });
                if (form.slug !== slug) navigate(`${ADMIN_BLOG_ROUTE}/${form.slug}`, { replace: true });
            } else {
                await blogApi.create(fd);
                navigate(ADMIN_BLOG_ROUTE);
            }
        } catch (err) {
            setToast({ type: "error", message: err.message });
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 p-4 sm:p-8">
            <Toast toast={toast} onClose={closeToast} />
            <div className="mx-auto max-w-4xl">
                <Link to={ADMIN_BLOG_ROUTE} className="text-sm text-gray-500 hover:text-gray-900">
                    ← Back to blogs
                </Link>
                <h1 className="mb-6 mt-2 text-2xl font-semibold text-gray-900">{isEdit ? "Edit blog" : "Create blog"}</h1>

                {loading ? (
                    <div className="flex items-center gap-3 py-16 text-gray-500">
                        <Spinner /> Loading blog…
                    </div>
                ) : loadError ? (
                    <p className="py-16 text-sm text-red-600">{loadError}</p>
                ) : (
                    <form onSubmit={onSubmit} className="space-y-6 rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
                        <Field label="Title" error={errors.title}>
                            <input className={inputCls} value={form.title} onChange={set("title")} />
                        </Field>

                        <div className="grid gap-6 sm:grid-cols-2">
                            <Field label="Slug" hint="Used in the blog URL" error={errors.slug}>
                                <input
                                    className={inputCls}
                                    value={form.slug}
                                    onChange={(e) => {
                                        setSlugTouched(true);
                                        set("slug")(e);
                                    }}
                                />
                            </Field>
                            <Field label="Industry ID" error={errors.industry_id}>
                                <input type="number" min="1" className={inputCls} value={form.industry_id} onChange={set("industry_id")} />
                            </Field>
                        </div>

                        <Field label="Author" error={errors.author}>
                            <input className={inputCls} value={form.author} onChange={set("author")} />
                        </Field>

                        <Field label="Short description" error={errors.short_description}>
                            <textarea rows={3} className={inputCls} value={form.short_description} onChange={set("short_description")} />
                        </Field>

                        <Field label="Content" hint="Separate paragraphs with a blank line" error={errors.content}>
                            <textarea rows={16} className={`${inputCls} font-mono leading-relaxed`} value={form.content} onChange={set("content")} />
                        </Field>

                        <Field label="Featured image" error={errors.featured_image} hint={isEdit ? "Leave empty to keep the current image" : undefined}>
                            <div className="flex items-start gap-4">
                                {imagePreview && <img src={imagePreview} alt="Featured preview" className="h-24 w-36 rounded-lg border border-gray-200 object-cover" />}
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={onImage}
                                    className="text-sm text-gray-600 file:mr-3 file:rounded-lg file:border-0 file:bg-gray-100 file:px-3 file:py-2 file:text-sm file:font-medium file:text-gray-700 hover:file:bg-gray-200"
                                />
                            </div>
                        </Field>

                        <label className="flex items-center gap-3">
                            <input type="checkbox" checked={form.is_published} onChange={set("is_published")} className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
                            <span className="text-sm font-medium text-gray-700">Published</span>
                        </label>

                        <div className="flex justify-end gap-3 border-t border-gray-100 pt-6">
                            <button type="button" onClick={() => navigate(ADMIN_BLOG_ROUTE)} className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                                Cancel
                            </button>
                            <button type="submit" disabled={saving} className="flex items-center gap-2 rounded-lg bg-indigo-600 px-5 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-60">
                                {saving && <Spinner className="h-4 w-4" />}
                                {isEdit ? "Save changes" : "Create blog"}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
}

/* ------------------------------------------------------------------ *
 * Routes (add to your router):
 *   <Route path="/superadmin/blogs" element={<BlogPageMaster />} />
 *   <Route path="/superadmin/blogs/new" element={<BlogEditor />} />
 *   <Route path="/superadmin/blogs/:slug" element={<BlogEditor />} />
 * ------------------------------------------------------------------ */